<template>
  <div class="container-fluid py-4">
    <div class="d-flex justify-content-between align-items-center mb-4 header-clinique flex-wrap gap-2">
      <div class="d-flex align-items-center gap-3">
        <i class="bi bi-shield-lock fs-2 text-primary"></i>
        <div>
          <h2 class="fw-bold mb-0">{{ id ? 'Modifier le rôle' : 'Créer un nouveau rôle' }}</h2>
          <small class="text-muted">Cochez ce que les personnes de ce rôle peuvent voir et faire. Les groupes suivent l'ordre du menu.</small>
        </div>
      </div>
      <router-link :to="{ name: 'role' }" class="btn btn-outline-secondary"><i class="bi bi-arrow-left me-1"></i>Retour</router-link>
    </div>

    <div v-if="chargement" class="text-center my-5"><div class="spinner-border text-primary"></div></div>

    <form v-else class="card shadow-sm card-clinique p-4" @submit.prevent="enregistrer">
      <div class="row g-3 align-items-end mb-3">
        <div class="col-md-5">
          <label for="nomRole" class="form-label fw-semibold">Nom du rôle</label>
          <input id="nomRole" v-model.trim="nom" type="text" class="form-control" placeholder="Ex. : Caissier, Médecin, Laborantin" required maxlength="100">
        </div>
        <div class="col-md-4">
          <label class="form-label fw-semibold">Rechercher une permission</label>
          <input v-model.trim="recherche" type="search" class="form-control" placeholder="Ex. : imprimer, caisse, laboratoire…">
        </div>
        <div class="col-md-3 text-md-end">
          <span class="badge bg-primary fs-6">{{ nombreCochees }} permission(s)</span>
        </div>
      </div>

      <div v-if="modeles.length" class="d-flex flex-wrap align-items-center gap-2 mb-3 p-2 rounded bg-white border">
        <label for="modeleRole" class="small fw-semibold mb-0"><i class="bi bi-magic me-1 text-primary"></i>{{ id ? 'Remettre les droits conseillés de :' : 'Partir des droits conseillés de :' }}</label>
        <select id="modeleRole" v-model="modele" class="form-select form-select-sm" style="width: auto">
          <option value="">— choisir un rôle type —</option>
          <option v-for="m in modeles" :key="m.code" :value="m.code">{{ m.nom }} ({{ m.permissions.length }} droits)</option>
        </select>
        <button type="button" class="btn btn-sm btn-outline-primary" :disabled="!modele" @click="appliquerModele">Appliquer</button>
        <small v-if="modeleChoisi" class="text-muted w-100">{{ modeleChoisi.description }} <router-link :to="{ name: 'aide', query: { metier: modeleChoisi.code } }" target="_blank">Guide de ce métier</router-link></small>
      </div>

      <div v-if="!toutAttribuable" class="alert alert-info small py-2">
        Vous ne pouvez donner que les permissions que vous avez vous-même. Les autres sont grisées.
      </div>

      <div class="row g-3">
        <div v-for="g in groupesFiltres" :key="g.code" class="col-lg-6">
          <div class="card h-100 groupe">
            <div class="card-header d-flex justify-content-between align-items-center bg-white">
              <div class="form-check mb-0">
                <input :id="`g-${g.code}`" type="checkbox" class="form-check-input" :checked="etatGroupe(g) === 'tout'" :indeterminate.prop="etatGroupe(g) === 'partiel'" @change="basculerGroupe(g, $event.target.checked)">
                <label :for="`g-${g.code}`" class="form-check-label fw-bold text-primary">{{ g.libelle }}</label>
              </div>
              <small class="text-muted">{{ compteGroupe(g) }}</small>
            </div>
            <div class="card-body py-2">
              <div v-for="(sg, i) in g.sousGroupes" :key="i" class="mb-2">
                <div v-if="sg.libelle" class="small fw-semibold text-secondary mt-1 mb-1">{{ sg.libelle }}</div>
                <div v-for="p in sg.permissions" :key="p.code" class="form-check">
                  <input :id="`p-${p.code}`" v-model="cochees" type="checkbox" class="form-check-input" :value="p.code" :disabled="!p.attribuable && !cochees.includes(p.code)">
                  <label :for="`p-${p.code}`" class="form-check-label" :class="{ 'text-muted': !p.attribuable }">
                    {{ p.libelle }}
                    <i v-if="!p.attribuable" class="bi bi-lock ms-1" title="Vous n'avez pas cette permission : vous ne pouvez pas la donner"></i>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="!groupesFiltres.length" class="col-12 text-muted">Aucune permission ne correspond à « {{ recherche }} ».</div>
      </div>

      <div v-if="erreur" class="alert alert-danger mt-3 mb-0">{{ erreur }}</div>

      <div class="d-flex justify-content-end gap-2 mt-4">
        <router-link :to="{ name: 'role' }" class="btn btn-secondary">Annuler</router-link>
        <button type="submit" class="btn btn-primary btn-lg" :disabled="enCours || !nom">
          <i class="bi bi-check2-circle me-1"></i>{{ enCours ? 'Enregistrement…' : (id ? 'Enregistrer les modifications' : 'Créer le rôle') }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import api, { messageErreur, notifier } from '@/api'

/**
 * Création et modification d'un rôle.
 * La liste des permissions vient du serveur (catalogue officiel, groupé comme
 * le menu, sans les modules absents de l'offre de la clinique).
 */
export default {
  name: 'FormulaireRole',
  props: { id: { type: Number, default: null } },
  data() {
    return {
      nom: '',
      cochees: [],
      autres: [], // codes enregistrés qui ne sont plus dans le catalogue : conservés tels quels
      groupes: [],
      alias: {},
      toutAttribuable: false,
      modeles: [],
      modele: '',
      recherche: '',
      chargement: true,
      enCours: false,
      erreur: '',
    }
  },
  computed: {
    codesCatalogue() {
      const c = []
      this.groupes.forEach(g => g.sousGroupes.forEach(sg => sg.permissions.forEach(p => c.push(p.code))))
      return c
    },
    groupesFiltres() {
      const q = this.normaliser(this.recherche)
      if (!q) return this.groupes
      return this.groupes
        .map(g => {
          const groupeCorrespond = this.normaliser(g.libelle).includes(q)
          const sousGroupes = g.sousGroupes
            .map(sg => ({ ...sg, permissions: groupeCorrespond || this.normaliser(sg.libelle).includes(q) ? sg.permissions : sg.permissions.filter(p => this.normaliser(p.libelle).includes(q)) }))
            .filter(sg => sg.permissions.length)
          return { ...g, sousGroupes }
        })
        .filter(g => g.sousGroupes.length)
    },
    nombreCochees() {
      return this.cochees.length
    },
    modeleChoisi() {
      return this.modeles.find(m => m.code === this.modele) || null
    },
    attribuables() {
      const c = []
      this.groupes.forEach(g => g.sousGroupes.forEach(sg => sg.permissions.forEach(p => { if (p.attribuable) c.push(p.code) })))
      return c
    },
  },
  async created() {
    try {
      const cat = (await api.custom('roles/catalogue-permissions', 'GET')).data
      this.groupes = cat.groupes
      this.alias = cat.alias || {}
      this.toutAttribuable = !!cat.toutAttribuable
      try {
        this.modeles = (await api.custom('roles/modeles', 'GET')).data.data || []
      } catch (e) {
        this.modeles = []
      }

      if (this.id) {
        const role = (await api.getItem('type_users', this.id)).data
        this.nom = role.nom
        const enregistrees = (role.permissions || [])
          .map(p => (typeof p === 'object' && p ? p.code : p))
          .filter(p => typeof p === 'string' && p.trim())
          .map(p => p.trim().toUpperCase())
          .map(p => this.alias[p] || p)
        this.cochees = [...new Set(enregistrees.filter(p => this.codesCatalogue.includes(p)))]
        this.autres = [...new Set(enregistrees.filter(p => !this.codesCatalogue.includes(p)))]
        // Rôle type reconnu à son nom : proposé pour remettre les droits conseillés
        this.modele = this.modeles.find(m => m.nom.toLowerCase() === String(role.nom || '').toLowerCase())?.code || ''
      }
    } catch (e) {
      this.erreur = messageErreur(e, 'Impossible de charger les permissions.')
    } finally {
      this.chargement = false
    }
  },
  methods: {
    normaliser(t) {
      return String(t || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    },
    codesGroupe(g, attribuablesSeulement = false) {
      const c = []
      g.sousGroupes.forEach(sg => sg.permissions.forEach(p => { if (!attribuablesSeulement || p.attribuable) c.push(p.code) }))
      return c
    },
    etatGroupe(g) {
      const codes = this.codesGroupe(g)
      const n = codes.filter(c => this.cochees.includes(c)).length
      return n === 0 ? 'rien' : n === codes.length ? 'tout' : 'partiel'
    },
    compteGroupe(g) {
      const codes = this.codesGroupe(g)
      return `${codes.filter(c => this.cochees.includes(c)).length} / ${codes.length}`
    },
    /**
     * Coche les droits conseillés du rôle type (seulement ceux qu'on peut donner
     * et ceux des modules de l'offre) ; le nom est repris s'il est vide.
     */
    appliquerModele() {
      const m = this.modeleChoisi
      if (!m) return
      const proposes = m.permissions.filter(c => this.codesCatalogue.includes(c))
      const donnes = this.toutAttribuable ? proposes : proposes.filter(c => this.attribuables.includes(c))
      this.cochees = [...new Set(donnes)]
      if (!this.nom) this.nom = m.nom
      const refuses = proposes.length - donnes.length
      notifier(`${donnes.length} droit(s) cochés selon le rôle type « ${m.nom} »${refuses ? ` ; ${refuses} que vous ne pouvez pas donner ont été laissés` : ''}. Ajustez puis enregistrez.`, 'info')
    },
    basculerGroupe(g, cocher) {
      const codes = this.codesGroupe(g, true)
      if (cocher) {
        this.cochees = [...new Set([...this.cochees, ...codes])]
      } else {
        this.cochees = this.cochees.filter(c => !codes.includes(c))
      }
    },
    async enregistrer() {
      if (!this.nom) return
      this.enCours = true
      this.erreur = ''
      const maintenant = new Date().toISOString()
      const payload = { nom: this.nom, permissions: [...this.cochees, ...this.autres], updatedAt: maintenant }
      try {
        if (this.id) {
          await api.updateItem('type_users', this.id, payload)
        } else {
          await api.createItem('type_users', { ...payload, isEnabled: true, createdAt: maintenant })
        }
        notifier(this.id ? 'Rôle enregistré.' : 'Rôle créé.', 'success')
        this.$router.push({ name: 'role' })
      } catch (e) {
        this.erreur = messageErreur(e, "Le rôle n'a pas pu être enregistré.")
      } finally {
        this.enCours = false
      }
    },
  },
}
</script>

<style scoped>
.header-clinique { padding: 15px 20px; background-color: #e9f5ff; border-radius: 12px; }
.card-clinique { background: #f8fafd; border-radius: 12px; border: none; }
.groupe { border-radius: 10px; border-color: #e3ebf5; }
.groupe .card-header { border-bottom-color: #eef3f9; }
.form-check-label { cursor: pointer; }
</style>
